// Webhooks dashboard — incoming (external → channel) + outgoing (message → external).
// Self-contained admin modal (inline glass styles, no shared CSS). Incoming shows the
// URL once on create; outgoing shows the signing secret once. Both are admin-only.
import { CSSProperties, useEffect, useState } from 'react';
import { api, Channel, IncomingWebhook, OutgoingWebhook } from './api';
import { Select } from './Select';

const chipBtn: CSSProperties = {
  border: '1px solid var(--fx-hair)', background: 'transparent', color: 'var(--fx-text)',
  borderRadius: 8, padding: '7px 11px', fontSize: 13, cursor: 'pointer', font: 'inherit',
};
const primaryBtn: CSSProperties = {
  ...chipBtn, background: 'var(--fx-accent)', color: '#1b1300', border: 'none', fontWeight: 700,
};
const field: CSSProperties = {
  width: '100%', boxSizing: 'border-box', border: '1px solid var(--fx-hair)', borderRadius: 8,
  background: 'transparent', color: 'var(--fx-text)', font: 'inherit', fontSize: 13, padding: '8px 10px',
};
const label: CSSProperties = {
  fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
  color: 'var(--fx-muted)', marginBottom: 5, display: 'block',
};
const card: CSSProperties = {
  border: '1px solid var(--fx-hair)', borderRadius: 10, padding: '11px 13px', marginBottom: 9,
};

function fmt(iso?: string | null): string {
  if (!iso) return 'never';
  return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// A one-time secret reveal (URL or signing secret) with copy.
function SecretReveal({ value, note }: { value: string; note: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ ...card, background: 'rgba(247,181,0,0.10)', borderColor: 'rgba(247,181,0,0.4)', marginTop: 4 }}>
      <div style={{ fontSize: 12, color: 'var(--fx-muted)', marginBottom: 6 }}>⚠️ {note}</div>
      <div style={{ display: 'flex', gap: 6 }}>
        <code style={{ ...field, overflowX: 'auto', whiteSpace: 'nowrap', fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{value}</code>
        <button type="button" style={primaryBtn}
          onClick={() => { navigator.clipboard?.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}>
          {copied ? 'Copied ✓' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

export function WebhooksModal({
  wid, channels, onClose,
}: {
  wid: string;
  channels: Channel[];
  onClose: () => void;
}) {
  const [tab, setTab] = useState<'incoming' | 'outgoing'>('incoming');
  const [incoming, setIncoming] = useState<IncomingWebhook[]>([]);
  const [outgoing, setOutgoing] = useState<OutgoingWebhook[]>([]);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  // New-incoming form
  const [inLabel, setInLabel] = useState('');
  const [inChannel, setInChannel] = useState('');
  const [newUrl, setNewUrl] = useState('');
  // New-outgoing form
  const [outUrl, setOutUrl] = useState('');
  const [outLabel, setOutLabel] = useState('');
  const [outChannel, setOutChannel] = useState('');
  const [outTrigger, setOutTrigger] = useState('');
  const [newSecret, setNewSecret] = useState('');

  const realChannels = channels.filter((c) => c.kind !== 'dm');

  const reload = () => {
    api.listIncomingWebhooks(wid).then(setIncoming).catch(() => {});
    api.listOutgoingWebhooks(wid).then(setOutgoing).catch(() => {});
  };
  useEffect(reload, [wid]);

  const createIncoming = async () => {
    if (!inChannel) { setErr('Pick a channel for the webhook to post into.'); return; }
    setBusy(true); setErr('');
    try {
      const h = await api.createIncomingWebhook(wid, inChannel, inLabel.trim() || 'Webhook');
      setNewUrl(h.url || '');
      setInLabel(''); setInChannel('');
      reload();
    } catch (e: any) { setErr(e?.message || 'Could not create the webhook.'); }
    finally { setBusy(false); }
  };
  const createOutgoing = async () => {
    if (!/^https?:\/\//.test(outUrl.trim())) { setErr('Enter a valid http(s) URL.'); return; }
    setBusy(true); setErr('');
    try {
      const h = await api.createOutgoingWebhook(wid, {
        url: outUrl.trim(), label: outLabel.trim() || undefined,
        channel_id: outChannel || undefined, trigger_word: outTrigger.trim() || undefined,
      });
      setNewSecret(h.secret || '');
      setOutUrl(''); setOutLabel(''); setOutChannel(''); setOutTrigger('');
      reload();
    } catch (e: any) { setErr(e?.message || 'Could not create the webhook.'); }
    finally { setBusy(false); }
  };
  const chName = (id?: string | null) => id ? (realChannels.find((c) => c.id === id)?.name || 'a channel') : 'all channels';

  return (
    <div className="fx-modal-backdrop" onClick={onClose}>
      <div className="fx-modal" onClick={(e) => e.stopPropagation()} style={{ width: 'min(620px, 94vw)', maxHeight: '86vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 750 }}>🔗 Webhooks</h3>
          <span style={{ flex: 1 }} />
          <button type="button" onClick={onClose} style={{ ...chipBtn, padding: '4px 9px' }}>Done</button>
        </div>
        <p style={{ margin: '0 0 14px', color: 'var(--fx-muted)', fontSize: 13 }}>
          Connect external tools: post <b>into</b> a channel with an incoming URL, or <b>send</b> new
          messages out to another service.
        </p>

        <div style={{ display: 'flex', gap: 6, marginBottom: 16, borderBottom: '1px solid var(--fx-hair)' }}>
          {(['incoming', 'outgoing'] as const).map((t) => (
            <button key={t} type="button" onClick={() => { setTab(t); setErr(''); }}
              style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '7px 10px', font: 'inherit',
                fontSize: 13.5, fontWeight: tab === t ? 750 : 500, color: tab === t ? 'var(--fx-text)' : 'var(--fx-muted)',
                borderBottom: `2px solid ${tab === t ? 'var(--fx-accent)' : 'transparent'}`, marginBottom: -1 }}>
              {t === 'incoming' ? 'Incoming' : 'Outgoing'}
            </button>
          ))}
        </div>

        {err && <div style={{ color: 'var(--fx-alert)', fontSize: 13, marginBottom: 12 }}>{err}</div>}

        {tab === 'incoming' && (
          <>
            {incoming.length === 0 && <div style={{ color: 'var(--fx-faint)', fontSize: 13, marginBottom: 10 }}>No incoming webhooks yet.</div>}
            {incoming.map((h) => (
              <div key={h.id} style={{ ...card, display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 650 }}>{h.label}</div>
                  <div style={{ fontSize: 12, color: 'var(--fx-muted)' }}>
                    → #{h.channel_name || 'channel'} · last used {fmt(h.last_used_at)}
                  </div>
                </div>
                <button type="button" style={{ ...chipBtn, color: 'var(--fx-alert)', borderColor: 'var(--fx-alert)' }}
                  onClick={async () => { if (confirm(`Revoke "${h.label}"? Its URL stops working immediately.`)) { await api.deleteIncomingWebhook(h.id).catch(() => {}); reload(); } }}>
                  Revoke
                </button>
              </div>
            ))}
            {newUrl && <SecretReveal value={newUrl} note={`Copy this URL now — it won't be shown again. POST JSON {"text":"…"} to it.`} />}
            <div style={{ ...card, marginTop: 12 }}>
              <div style={{ ...label }}>New incoming webhook</div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input value={inLabel} onChange={(e) => setInLabel(e.target.value)} placeholder="Label (e.g. GitHub)" style={{ ...field, flex: 1 }} />
                <Select value={inChannel} onChange={setInChannel} placeholder="Post into…" style={{ flex: 1 }}
                  options={realChannels.map((c) => ({ value: c.id, label: `#${c.name}` }))} />
              </div>
              <button type="button" style={primaryBtn} disabled={busy} onClick={createIncoming}>Create incoming webhook</button>
            </div>
          </>
        )}

        {tab === 'outgoing' && (
          <>
            {outgoing.length === 0 && <div style={{ color: 'var(--fx-faint)', fontSize: 13, marginBottom: 10 }}>No outgoing webhooks yet.</div>}
            {outgoing.map((h) => (
              <div key={h.id} style={{ ...card, display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 650, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {h.label || h.url}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--fx-muted)' }}>
                    from {chName(h.channel_id)}{h.trigger_word ? ` · trigger "${h.trigger_word}"` : ''}
                    {' · '}last {h.last_status ? `HTTP ${h.last_status}` : 'undelivered'}
                  </div>
                </div>
                <button type="button" style={{ ...chipBtn, color: 'var(--fx-alert)', borderColor: 'var(--fx-alert)' }}
                  onClick={async () => { if (confirm('Delete this outgoing webhook?')) { await api.deleteOutgoingWebhook(h.id).catch(() => {}); reload(); } }}>
                  Delete
                </button>
              </div>
            ))}
            {newSecret && <SecretReveal value={newSecret} note="Signing secret — copy it now. We sign each delivery: X-FxM-Signature = HMAC-SHA256(secret, timestamp.body)." />}
            <div style={{ ...card, marginTop: 12 }}>
              <div style={{ ...label }}>New outgoing webhook</div>
              <input value={outUrl} onChange={(e) => setOutUrl(e.target.value)} placeholder="https://example.com/hook" style={{ ...field, marginBottom: 8 }} />
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input value={outLabel} onChange={(e) => setOutLabel(e.target.value)} placeholder="Label (optional)" style={{ ...field, flex: 1 }} />
                <Select value={outChannel} onChange={setOutChannel} style={{ flex: 1 }}
                  options={[{ value: '', label: 'All channels' }, ...realChannels.map((c) => ({ value: c.id, label: `#${c.name}` }))]} />
              </div>
              <input value={outTrigger} onChange={(e) => setOutTrigger(e.target.value)} placeholder="Trigger word (optional — only fire when present)" style={{ ...field, marginBottom: 8 }} />
              <button type="button" style={primaryBtn} disabled={busy} onClick={createOutgoing}>Create outgoing webhook</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
